import { useCallback, useEffect, useState } from 'react';

export type ChatPanelSize = 'default' | 'wide';

// Matches the breakpoint the panel stylesheet uses for its full-screen sheet.
const MOBILE_MEDIA_QUERY = '(max-width: 47.99rem)';

const PANEL_SIZE_STORAGE_KEY = 'kudu.chat.panelSize';

function isMobileViewport() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
    : false;
}

function getStoredSize(): ChatPanelSize {
  if (typeof window === 'undefined') {
    return 'default';
  }

  return window.localStorage.getItem(PANEL_SIZE_STORAGE_KEY) === 'wide'
    ? 'wide'
    : 'default';
}

export interface ChatPanelSizeState {
  // False on narrow viewports, where the panel is already full screen and a
  // size control has nothing to offer.
  canResize: boolean;
  isMobile: boolean;
  size: ChatPanelSize;
  toggleSize: () => void;
}

// Owns the panel's size preference.
//
// The preference persists so a student who wants room to read keeps it — the
// same treatment the instructor control panel gives its expanded state. It is
// stored regardless of viewport, so switching to a wide screen restores the
// choice rather than silently resetting it.
export function useChatPanelSize(): ChatPanelSizeState {
  const [size, setSize] = useState<ChatPanelSize>(getStoredSize);
  const [isMobile, setIsMobile] = useState(isMobileViewport);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    // Safari < 14 only has the deprecated listener API.
    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, []);

  const toggleSize = useCallback(() => {
    setSize((current) => {
      const next: ChatPanelSize = current === 'wide' ? 'default' : 'wide';

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(PANEL_SIZE_STORAGE_KEY, next);
      }

      return next;
    });
  }, []);

  return {
    canResize: !isMobile,
    isMobile,
    // On mobile the panel is a full-screen sheet already; reporting 'default'
    // keeps the stylesheet from applying desktop wide-mode geometry to it.
    size: isMobile ? 'default' : size,
    toggleSize,
  };
}
